import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import LogoDark from "../../images/logo/logo-dark.png";
import userService from "../../services/userService";
import { Messages } from "../../constants/Messages";

interface RegistrationFlowData {
    email: string;
    nombre?: string;
    apellido?: string;
    estado?: string;
    legajo?: string;
    codigoColegioActual?: string;
}

const SignupImagen: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [cameraKey, setCameraKey] = useState(0);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [showSkipModal, setShowSkipModal] = useState(false);
    const [data, setData] = useState<RegistrationFlowData | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [showCamera, setShowCamera] = useState(false);
    const btnPrimarySm =
        "px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 transition";

    const btnOutlineSm =
        "px-4 py-2 rounded-lg border border-primary text-primary text-sm font-medium hover:bg-primary/5 transition";

    const videoRef = useRef<HTMLVideoElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const resetPhoto = () => {
        setSelectedImage(null);
        setSelectedFile(null);
        cancelCamera();
    };
    /* ================== DATA ================== */
    useEffect(() => {
        const stateData = location.state as RegistrationFlowData | null;
        if (stateData?.email) {
            setData(stateData);
            sessionStorage.setItem("verifyEmailData", JSON.stringify(stateData));
        } else {
            const stored = sessionStorage.getItem("verifyEmailData");
            if (stored) setData(JSON.parse(stored));
        }
    }, [location.state]);

    // Mapeo del código de colegio al nombre mediante Messages.colegio
    const colegioNombre = React.useMemo(() => {
        const codigo = data?.codigoColegioActual;
        if (!codigo) return "—";
        const item = Messages.colegio.find((c) => c.id === codigo);
        return item?.value ?? codigo;
    }, [data]);

    useEffect(() => {
        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(t => t.stop());
            }
        };
    }, []);


    /* ================== CÁMARA ================== */
    const openCamera = async () => {
        try {
            setShowCamera(false);

            // destruir stream previo
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(t => t.stop());
                streamRef.current = null;
            }

            // forzar recreación del <video>
            setCameraKey(prev => prev + 1);

            await new Promise(res => setTimeout(res, 200));

            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: "user",
                },
                audio: false,
            });

            streamRef.current = stream;

            setShowCamera(true);

            // esperar a que el video exista en el DOM
            await new Promise(res => setTimeout(res, 200));

            if (videoRef.current) {
                videoRef.current.srcObject = stream;

                videoRef.current.onloadedmetadata = async () => {
                    try {
                        await videoRef.current?.play();
                    } catch (e) {
                        console.error("No se pudo reproducir", e);
                    }
                };
            }
        } catch (err) {
            console.error(err);
            alert("No se pudo acceder a la cámara");
            setShowCamera(false);
        }
    };
    const takePhoto = () => {
        if (!videoRef.current || !canvasRef.current) return;

        const video = videoRef.current;
        if (video.readyState < 2 || video.videoWidth === 0) {
            alert("La cámara todavía no está lista");
            return;
        }

        const canvas = canvasRef.current;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob((blob) => {
            if (!blob) return;

            const file = new File([blob], "perfil.jpg", { type: "image/jpeg" });
            setSelectedFile(file);
            setSelectedImage(URL.createObjectURL(blob));
        }, "image/jpeg");

        cancelCamera();
    };
    const cancelCamera = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
        setShowCamera(false);
    };

    /* ================== ESTILOS ================== */
    const labelClass = "mb-1 block text-sm font-medium text-gray-600";
    const readonlyField = "w-full rounded-lg bg-gray-50 border border-stroke px-4 py-3";
    const readonlyValue = "text-sm font-medium text-gray-900";
    const cardClass = "bg-white rounded-xl border border-stroke";

    return (
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">

            {/* ================= IZQUIERDA ================= */}
            <div className="flex flex-col justify-center px-6 py-8 border-b border-stroke bg-gray-50 lg:border-b-0 lg:border-r lg:px-10 lg:py-0">
                {/* Logo */}
                <div className="flex flex-col items-center mb-6 lg:mb-8">
                    <img
                        src={LogoDark}
                        alt="Martilleros BA"
                        className="h-10"
                    />
                </div>

                {/* Card datos */}
                <div className="bg-white rounded-xl border border-stroke p-5 max-w-md mx-auto w-full">
                    <h2 className="text-lg font-semibold mb-1">
                        Tus datos
                    </h2>
                    <p className="text-sm text-gray-500 mb-4">
                        Consultá la información de tu cuenta
                    </p>
                    <div className="mb-4">
                        {/* Nombre + punto */}
                        <div className="flex items-center gap-2">
                            <div className={readonlyField}>
                                <p className={readonlyValue}>
                                    {data?.nombre} {data?.apellido}
                                </p>

                                <span
                                    className={`h-2 w-2 rounded-full ${data?.estado === "Afiliado Activo"
                                        ? "bg-green-500"
                                        : "bg-red-500"
                                        }`}
                                />
                                {/* Estado */}
                                <p className="mt-1 text-sm text-gray-500">
                                    {data?.estado}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Correo */}
                    <div className="mb-4">
                        <label className={labelClass}>
                            Correo
                        </label>
                        <div className={readonlyField}>
                            <p className={readonlyValue}>
                                {data?.email}
                            </p>
                        </div>
                    </div>

                    {/* Legajo */}
                    <div className="mb-4">
                        <label className={labelClass}>
                            Legajo previsional
                        </label>
                        <div className={readonlyField}>
                            <p className={readonlyValue}>
                                {data?.legajo}
                            </p>
                        </div>
                    </div>

                    {/* Colegio */}
                    <div>
                        <label className={labelClass}>
                            Colegio
                        </label>
                        <div className={readonlyField}>
                            <p className={readonlyValue}>
                                {colegioNombre}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* ================= DERECHA ================= */}
            <div className="flex items-center justify-center px-4 sm:px-6 py-6">
                <div className="w-full max-w-md mx-auto">
                    <div className={cardClass}>

                        <div className="border-b border-stroke px-6 py-4">
                            <h3 className="font-semibold">Tu foto</h3>
                        </div>

                        <div className="p-6 space-y-6 text-center">
                            <div className="flex flex-col items-center gap-4">

                                {/* PREVIEW */}
                                {selectedImage ? (
                                    <img
                                        src={selectedImage}
                                        alt="Preview"
                                        className="h-48 w-48 rounded-full object-cover border border-gray-300"
                                    />
                                ) : showCamera ? (
                                    <div className="relative h-48 w-48 rounded-full border border-gray-300 overflow-hidden bg-black">
                                        <video
                                            key={cameraKey}
                                            ref={videoRef}
                                            muted
                                            playsInline
                                            autoPlay
                                            className="absolute inset-0 w-full h-full object-cover"
                                        />
                                    </div>
                                ) : (
                                    <div className="h-48 w-48 rounded-full bg-gray-100 flex items-center justify-center border border-gray-300">
                                        📷
                                    </div>
                                )}
                                {selectedImage && (
                                    <button
                                        onClick={resetPhoto}
                                        className="text-sm text-primary hover:underline"
                                    >
                                        Cambiar foto
                                    </button>
                                )}

                                <div className="flex gap-3 justify-center flex-wrap">
                                    {/* Seleccionar Imagen */}
                                    <label className={`cursor-pointer ${btnOutlineSm}`}>
                                        Seleccionar imagen
                                        <input
                                            type="file"
                                            accept="image/*"
                                            hidden
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                    setSelectedFile(file);
                                                    setSelectedImage(URL.createObjectURL(file));
                                                }
                                            }}
                                        />
                                    </label>

                                    {/* Tomar Foto */}
                                    <button
                                        onClick={() => {
                                            setSelectedImage(null);
                                            openCamera();
                                        }}
                                        className={btnPrimarySm}
                                    >
                                        Tomar foto
                                    </button>
                                </div>


                                {showCamera && (
                                    <div className="flex gap-2 mt-2">
                                        <button
                                            onClick={takePhoto}
                                            className="px-4 py-2 bg-primary text-white rounded"
                                        >
                                            Sacar Foto
                                        </button>
                                        <button
                                            onClick={cancelCamera}
                                            className="px-4 py-2 bg-gray-300 rounded"
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                )}
                            </div>

                            <p className="text-sm text-gray-500">
                                PNG, JPG (máx 800 x 800px)
                            </p>

                            <canvas ref={canvasRef} style={{ display: "none" }} />
                        </div>

                        <div className="flex gap-4 px-6 pb-6">
                            <button
                                className="w-full bg-primary text-white py-3 rounded-lg"
                                onClick={async () => {
                                    if (!selectedFile) {
                                        setShowSkipModal(true);
                                        return;
                                    }

                                    try {
                                        const response = await userService.uploadImage(
                                            data?.legajo || "",   // o el afi_id real
                                            selectedFile
                                        );

                                        if (response.success) {
                                            navigate("/SignupExito"); // o SignupExito
                                        } else {
                                            alert(response.errors?.[0] || "Error al subir la imagen");
                                        }
                                    } catch (err: any) {
                                        alert("Error al subir la imagen");
                                        console.error(err);
                                    }
                                }}
                            >
                                Continuar
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* MODAL */}
            {showSkipModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div className="w-full max-w-md rounded-xl bg-white shadow-lg">
                        <div className="border-b border-stroke px-6 py-4">
                            <h3 className="text-lg font-semibold">
                                ¿Seguro que no querés subir una foto ahora?
                            </h3>
                        </div>
                        <div className="px-6 py-4 text-sm text-gray-600">
                            Tu foto ayuda a tu perfil profesional.
                        </div>
                        <div className="flex gap-3 px-6 pb-6">
                            <button
                                className="w-full border border-primary text-primary py-3 rounded-lg"
                                onClick={() => navigate("/SignupExito")}
                            >
                                Omitir por ahora
                            </button>
                            <button
                                className="w-full bg-primary text-white py-3 rounded-lg"
                                onClick={() => setShowSkipModal(false)}
                            >
                                Subir foto ahora
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SignupImagen;
